import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, AlertTriangle, Download, Info, RefreshCw } from 'lucide-react';
import {
  listPadrones,
  getDashboardGlobal,
  getEleccionDelUsuario,
  listarObservacionesPendientes,
  listarPreexistenciasPendientes,
  listarCoberturaPrivadaPendientes,
  type DashboardLegajosGlobal,
  type DesgloseEleccionDelUsuario,
  type DesgloseEnValidacion,
  type LegajoConObservaciones,
  type LegajoConPreexistencias,
  type LegajoConCoberturaPrivada,
  type ObservacionPendiente,
  type PreexistenciaItem,
  type CoberturaPrivadaItem,
} from '@lib/services/padrones.service';
import { ExportModal } from '@components/ExportModal';
import { useAuthStore } from '@lib/store/auth.store';
import { isMedicus, UserRole } from '@lib/types';
import { CardsSkeleton } from '@components/medicus-ui/Loaders';
import { estadoLegajoInfo } from '@lib/estado-legajo';

/**
 * Estados del legajo que se ven en el funnel del dashboard, en orden del flujo.
 * Incluye `dado_de_alta` (cierre exitoso) al final y `rechazado_binary` (rebotó
 * en Binary — antes no se mostraba en ningún lado). Se omite `rechazado_medicus`
 * (gate que no se usa) y `legajo_bis` (tiene su propia vista Legajos bis).
 */
const ORDEN_ESTADOS = [
  'pendiente_validacion_medicus',
  'creado',
  'en_validacion',
  'validado_por_titular',
  'en_alta_binary',
  'dado_de_alta',
  'rechazado_binary',
] as const;

// Los LABELS de cada estado salen del catálogo único (estadoLegajoInfo). El
// color de las barras del funnel se mantiene local (paleta distinta por paso
// para legibilidad del embudo, no la del badge).
const ESTADO_COLOR: Record<string, string> = {
  pendiente_validacion_medicus: '#92400E',
  rechazado_medicus: '#B91C1C',
  creado: '#1D4ED8',
  en_validacion: '#B45309',
  validado_por_titular: '#0E7490',
  en_alta_binary: '#7C3AED',
  dado_de_alta: '#166534',
  rechazado_binary: '#991B1B',
};

/**
 * Estados que tienen sub-funnel/desglose:
 *  - `en_validacion`: paso del wizard donde se quedó el titular (datos
 *    titular -> datos GF -> cuestionario -> elección de plan).
 *
 * El desglose de `validado_por_titular` (decisión final MS1/MS2/contacto
 * comercial) se movió al widget independiente `EleccionDelUsuarioWidget`
 * — agregado abajo del dashboard y restringido a roles Medicus, porque
 * la sección Legajos se está abriendo a más roles que no deben ver ese KPI.
 */
const ESTADOS_CON_SUBFUNNEL = new Set(['en_validacion']);

export function DashboardPage() {
  const user = useAuthStore((s) => s.user);

  const padrones = useQuery({ queryKey: ['padrones'], queryFn: listPadrones });
  const dashboard = useQuery({
    queryKey: ['dashboard-global'],
    queryFn: () => getDashboardGlobal(),
    // El endpoint cachea server-side por scope (TTL 60s) — con varios admins
    // mirando, comparten una sola entrada. Acá polleamos cada 120s (no 60s)
    // para reducir el chatter de requests. react-query ya pausa el polling
    // cuando la pestaña está en background (refetchIntervalInBackground=false
    // por default), así que las pestañas olvidadas no martillan.
    refetchInterval: 120_000,
  });

  const total = padrones.data?.reduce((acc, p) => acc + p.totalRegistros, 0) ?? 0;
  const ok = padrones.data?.reduce((acc, p) => acc + p.registrosOk, 0) ?? 0;
  const errs = padrones.data?.reduce((acc, p) => acc + p.registrosError, 0) ?? 0;
  // "Asociados OK" suma TODOS los padrones. Desglosamos cerrados (ya tienen
  // legajo) vs el resto en borrador (cargados OK pero sin cerrar → sin legajo
  // todavía), que es lo que hace que el número quede por encima del embudo.
  const okCerrado =
    padrones.data?.filter((p) => p.estado === 'cerrado').reduce((acc, p) => acc + p.registrosOk, 0) ?? 0;
  const okBorrador = ok - okCerrado;

  return (
    <section>
      <header
        className="pageHeader"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}
      >
        <div>
          <h1 className="heading-3">Dashboard</h1>
          <p className="text-element-mid">
            Vista general de los padrones cargados.
          </p>
        </div>
        <DashboardRefetchButton />
      </header>

      <div className="grid grid-cols-3 gap-md" style={{ marginTop: '1.5rem' }}>
        <article className="card">
          <p className="text-element-mid">Padrones</p>
          <p className="heading-2 text-primary">{padrones.data?.length ?? '—'}</p>
        </article>
        <article className="card">
          <p className="text-element-mid">Asociados OK</p>
          <p className="heading-2 text-success">{ok.toLocaleString('es-AR')}</p>
          {okBorrador > 0 && (
            <p className="text-element-mid" style={{ fontSize: 12, marginTop: 2 }}>
              {okCerrado.toLocaleString('es-AR')} en padrones cerrados ·{' '}
              {okBorrador.toLocaleString('es-AR')} en borrador
            </p>
          )}
        </article>
        <article className="card">
          <p className="text-element-mid">Inconsistencias</p>
          <p className="heading-2 text-error">{errs.toLocaleString('es-AR')}</p>
        </article>
      </div>

      <LegajosWidget query={dashboard} />

      {/* Observaciones y Preexistencias se renderizan como widgets propios
          fuera del widget Legajos. Hasta el 28/05 vivían como banners
          adentro de la card de Legajos; se aislaron para que la UI refleje
          que son flujos independientes (resolver observaciones / analizar
          preexistencias) y no "una variante más" del funnel de alta. */}
      {dashboard.data && (
        <>
          {dashboard.data.observacionesPendientes > 0 && (
            <ObservacionesWidget
              total={dashboard.data.observacionesPendientes}
              totalPersonas={dashboard.data.observacionesPendientesPersonas}
            />
          )}
          {dashboard.data.preexistenciasDeclaradas > 0 && (
            <PreexistenciasWidget
              total={dashboard.data.preexistenciasDeclaradas}
              totalPersonas={dashboard.data.preexistenciasDeclaradasPersonas}
            />
          )}
          {dashboard.data.estadosPersona && (
            <EstadosPersonaWidget datos={dashboard.data.estadosPersona} />
          )}
        </>
      )}

      {user && <EleccionDelUsuarioWidget />}

      <div className="card" style={{ marginTop: '1.5rem' }}>
        <div className="rowBetween">
          <h2 className="heading-5">Acciones rápidas</h2>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.75rem' }}>
          <Link to="/padrones" className="btn btn-primary">
            <span className="btn-font-style">Subir padrón</span>
          </Link>
        </div>
        <p className="text-element-mid" style={{ marginTop: '0.75rem' }}>
          Total de registros procesados: <strong>{total.toLocaleString('es-AR')}</strong>
        </p>
      </div>
    </section>
  );
}

/**
 * Botón sutil de "Actualizar" en la esquina superior derecha del dashboard.
 * Trae los datos REALES salteando el cache server-side (TTL 60s) con `?fresh=1`
 * y refresca las queries del panel (dashboard-global, padrones, elección). El
 * server reescribe su cache con el valor fresco, así no se desincroniza con el
 * polling. Mientras corre, el ícono gira.
 */
function DashboardRefetchButton() {
  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const [fallo, setFallo] = useState(false);

  async function actualizar() {
    if (refreshing) return;
    setRefreshing(true);
    setFallo(false);
    try {
      const [dashboard, padronesData, eleccion] = await Promise.all([
        getDashboardGlobal({ fresh: true }),
        listPadrones(),
        getEleccionDelUsuario({ fresh: true }),
      ]);
      queryClient.setQueryData(['dashboard-global'], dashboard);
      queryClient.setQueryData(['padrones'], padronesData);
      queryClient.setQueryData(['eleccion-del-usuario'], eleccion);
    } catch {
      // Si el refetch directo falla, invalidamos para que las queries reintenten
      // solas — y se avisa: antes el ícono dejaba de girar y los datos viejos
      // quedaban igual, indistinguible de un refresh que salió bien.
      setFallo(true);
      await queryClient.invalidateQueries({ queryKey: ['dashboard-global'] });
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <button
      type="button"
      onClick={() => {
        actualizar().catch(() => undefined);
      }}
      disabled={refreshing}
      className="btn btn-ghost btn--sm"
      title="Actualizar con datos reales (omite el cache de 60s)"
      aria-label="Actualizar datos"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 10px',
        color: 'var(--color-element-mid)',
        flexShrink: 0,
      }}
    >
      <RefreshCw
        size={14}
        style={{ animation: refreshing ? 'spin 0.65s linear infinite' : undefined }}
      />
      <span style={{ fontSize: 12 }}>
        {refreshing ? 'Actualizando…' : fallo ? 'No se pudo actualizar · reintentar' : 'Actualizar'}
      </span>
    </button>
  );
}

/**
 * Widget de legajos: funnel por estado + desglose del wizard al hacer doble
 * click sobre `en_validacion`. Si el user no tiene legajos visibles (recien
 * llegado o sin permisos) muestra un placeholder.
 */
function LegajosWidget({
  query,
}: {
  query: ReturnType<typeof useQuery<DashboardLegajosGlobal>>;
}) {
  // Set de estados expandidos. La UX es: doble-click en una fila con
  // sub-funnel expande/colapsa el detalle inline.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  function toggleExpanded(estado: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(estado)) next.delete(estado);
      else next.add(estado);
      return next;
    });
  }

  if (query.isLoading) {
    return (
      <div style={{ marginTop: '1.5rem' }}>
        <CardsSkeleton count={6} />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="card" style={{ marginTop: '1.5rem' }}>
        <p className="status status-error" style={{ display: 'block' }}>
          No se pudo cargar el resumen de legajos.
        </p>
      </div>
    );
  }

  const d = query.data;
  // Total del header = suma de los pasos del embudo (ORDEN_ESTADOS). Excluye
  // `legajo_bis` (se muestra aparte y DUPLICA personas: el mismo individuo está
  // en su legajo y en el duplicado bis) y `rechazado_medicus`. Sin esto el total
  // de personas quedaba INFLADO y mayor que "Asociados OK".
  const legTotal = ORDEN_ESTADOS.reduce((acc, e) => acc + (d.porEstado[e] ?? 0), 0);
  const perTotal = ORDEN_ESTADOS.reduce((acc, e) => acc + (d.personasPorEstado[e] ?? 0), 0);

  return (
    <section className="card" style={{ marginTop: '1.5rem' }}>
      <div className="rowBetween" style={{ alignItems: 'baseline' }}>
        <div>
          <h2 className="heading-5" style={{ margin: 0 }}>
            Legajos
          </h2>
          <p className="text-element-mid" style={{ fontSize: 12, marginTop: 2 }}>
            {d.scope.empresaIds === null
              ? 'Todos los padrones del sistema.'
              : `Acotado a ${d.scope.empresaIds.length} empresa${d.scope.empresaIds.length === 1 ? '' : 's'} a las que tenés acceso.`}
            {' '}
            Click en un estado con sub-pasos para ver el desglose.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <strong
            className="heading-2"
            style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--color-primary-darker)' }}
          >
            {legTotal.toLocaleString('es-AR')}
          </strong>
          <PersonasParen total={perTotal} />
          <InfoTooltip texto="Total del embudo (suma de los estados de abajo). No cuenta los integrantes a sumar: duplican personas (el mismo individuo está en su legajo y en el grupo a sumar) y se ven en su propia sección. Por eso el total de personas no supera a 'Asociados OK'." />
          {legTotal > 0 && <MetricaDownloadButton metric="total" titulo="Todos los legajos" />}
        </div>
      </div>

      {legTotal === 0 && (
        <p className="text-element-mid" style={{ marginTop: 12, fontSize: 13 }}>
          Todavía no hay legajos. Cuando una empresa cierre un padrón y un admin Medicus lo valide,
          van a aparecer acá.
        </p>
      )}

      {legTotal > 0 && (
        <>
          {/* Cobertura privada queda como banner dentro de Legajos: a diferencia
              de observaciones / preexistencias, no es un flow de trabajo
              separado del admin Medicus — es un dato informativo del cuestionario
              que puede impactar comercialmente y se ve mejor "junto" al funnel. */}
          {d.coberturaPrivadaDeclarada > 0 && (
            <CoberturaPrivadaBanner
              total={d.coberturaPrivadaDeclarada}
              totalPersonas={d.coberturaPrivadaDeclaradaPersonas}
            />
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 16 }}>
            {ORDEN_ESTADOS.map((estado) => {
              // Neto disponible para alta: se restan los bloqueados SOLO en los
              // pasos pre-alta. Personas: toda persona bloqueada. Legajos: solo
              // los que tienen el TITULAR bloqueado (si el bloqueado es un
              // familiar el legajo igual va, por eso no se descuenta de legajos).
              // En `en_alta_binary`/`dado_de_alta` NO se resta: ya se enviaron /
              // se dieron de alta; el bloqueo no los revierte.
              const yaEnviado = estado === 'en_alta_binary' || estado === 'dado_de_alta';
              const bloq = yaEnviado
                ? { legajos: 0, personas: 0 }
                : d.bloqueadosPorEstado?.[estado] ?? { legajos: 0, personas: 0 };
              // La baja SÍ se resta en TODOS los estados (incluido dado_de_alta):
              // a diferencia del bloqueo (freno pre-alta), una persona de baja ya
              // no es un alta activa. Un titular de baja además descuenta el legajo.
              const baja = d.bajasPorEstado?.[estado] ?? { legajos: 0, personas: 0 };
              const count = Math.max(0, (d.porEstado[estado] ?? 0) - bloq.legajos - baja.legajos);
              const personas = Math.max(0, (d.personasPorEstado[estado] ?? 0) - bloq.personas - baja.personas);
              const pct = legTotal > 0 ? (count / legTotal) * 100 : 0;
              const tieneSubFunnel = ESTADOS_CON_SUBFUNNEL.has(estado);
              const isExpanded = expanded.has(estado);
              return (
                <div key={estado}>
                  <FunnelRow
                    label={estadoLegajoInfo(estado).label}
                    descripcion={estadoLegajoInfo(estado).descripcion}
                    count={count}
                    countPersonas={personas}
                    pct={pct}
                    color={ESTADO_COLOR[estado] ?? '#475569'}
                    expandible={tieneSubFunnel && count > 0}
                    expanded={isExpanded}
                    onClick={() => tieneSubFunnel && count > 0 && toggleExpanded(estado)}
                    metric={`estado.${estado}`}
                    tituloDescarga={estadoLegajoInfo(estado).label}
                  />
                  {/* Paso pre-alta con legajos frenados por titular bloqueado: el
                      número principal es el NETO disponible; acá se explicita
                      disponible · frenado · total para que no parezca vacío. */}
                  {bloq.legajos > 0 && (
                    <p
                      className="text-element-mid"
                      style={{ fontSize: 11, margin: '2px 0 0', paddingLeft: 8 }}
                    >
                      {count.toLocaleString('es-AR')} disponibles ·{' '}
                      <strong style={{ color: '#b45309' }}>
                        {bloq.legajos.toLocaleString('es-AR')} frenados por bloqueo
                      </strong>{' '}
                      · {(d.porEstado[estado] ?? 0).toLocaleString('es-AR')} total
                    </p>
                  )}
                  {tieneSubFunnel && isExpanded && (
                    <SubFunnelRenderer estado={estado} dashboard={d} />
                  )}
                </div>
              );
            })}

            {/* "En observación" es TRANSVERSAL (no un estado del funnel): legajos
                con observaciones pendientes, que pueden estar en cualquier
                estado. Se muestra abajo, separado, para no leerse como parte de
                la suma del embudo. */}
            {d.observacionesPendientes > 0 && (
              <div
                style={{
                  marginTop: 6,
                  paddingTop: 8,
                  borderTop: '1px dashed var(--color-border, #E2E8F0)',
                }}
              >
                <FunnelRow
                  label="En observación"
                  descripcion="Legajos con observaciones del titular sin resolver (reportó un dato como incorrecto). Bloquean el alta hasta resolverlas. Es transversal: el legajo está además en alguno de los estados de arriba."
                  count={d.observacionesPendientes}
                  countPersonas={d.observacionesPendientesPersonas}
                  pct={legTotal > 0 ? (d.observacionesPendientes / legTotal) * 100 : 0}
                  color="#0891B2"
                  expandible={false}
                  expanded={false}
                  onClick={() => undefined}
                  metric="observacion"
                  tituloDescarga="En observación"
                />
                <p
                  className="text-element-mid"
                  style={{ fontSize: 11, margin: '2px 0 0', paddingLeft: 8 }}
                >
                  Transversal: legajos con observaciones pendientes (puede solaparse con los
                  estados de arriba).
                </p>
              </div>
            )}

            {/* "Bloqueados" TRANSVERSAL: personas con el flag `bloqueado` (lista
                EUGE). YA se restaron de cada paso de arriba → los pasos muestran
                el neto disponible para alta. Acá va el total frenado. */}
            {(() => {
              // Total de lo restado: solo pasos pre-alta (no en_alta_binary/dado_de_alta).
              const bloqEntries = Object.entries(d.bloqueadosPorEstado ?? {}).filter(
                ([e]) => e !== 'en_alta_binary' && e !== 'dado_de_alta',
              );
              // Bloqueados sacados del alta que ya no están en ningún legajo
              // (los identifica la reconciliación de cerrados). NO están en
              // ningún paso de arriba, así que se suman aparte para que el total
              // de bloqueados los incluya.
              const sinLegajoBloq = d.cerradosReconciliacion?.bloqueados ?? 0;
              const totLeg = bloqEntries.reduce((a, [, b]) => a + b.legajos, 0);
              const totPer = bloqEntries.reduce((a, [, b]) => a + b.personas, 0) + sinLegajoBloq;
              if (totPer === 0) return null;
              return (
                <div style={{ marginTop: 6, paddingTop: 8, borderTop: '1px dashed var(--color-border, #E2E8F0)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-element-high)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ width: 14 }} />
                      Bloqueados
                      <InfoTooltip texto="Personas con el flag 'bloqueado' (lista EUGE). Si el titular está bloqueado, el legajo entero queda frenado. Ya están descontados de cada paso de arriba — los pasos muestran el neto disponible para pasar al alta." />
                    </span>
                    <span style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', color: '#64748B', display: 'flex', alignItems: 'center', gap: 8 }}>
                      {totLeg.toLocaleString('es-AR')} legajos
                      <PersonasParen total={totPer} tone="#64748B" />
                    </span>
                  </div>
                </div>
              );
            })()}

            {/* "Legajos bis" TRANSVERSAL: duplicados que NO entran al embudo. Son
                la razón principal de que "Asociados OK" (cerrados) quede por
                encima del total del embudo — esa gente está en un bis en vez de
                en su legajo real. */}
            {(() => {
              const bisLeg = d.porEstado['legajo_bis'] ?? 0;
              // Sólo INTEGRANTES (adicionales): el titular del bis ya está contado
              // en su legajo real del embudo, así que NO se suma. Se usa `soloBis`
              // (personas cuya única presencia es un bis) para que la cuenta cierre;
              // fallback a (miembros − legajos) si el backend todavía no lo trae.
              const bisInteg =
                d.cerradosReconciliacion?.soloBis ??
                Math.max(0, (d.personasPorEstado['legajo_bis'] ?? 0) - bisLeg);
              if (bisLeg === 0) return null;
              return (
                <div style={{ marginTop: 6, paddingTop: 8, borderTop: '1px dashed var(--color-border, #E2E8F0)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-element-high)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ width: 14 }} />
                      Alta Integrantes
                      <InfoTooltip texto="Integrantes por resolver: llegaron con un número de legajo que ya existía, así que NO entran al embudo. El conteo de personas suma SOLO los integrantes (adicionales) que aportan: el titular ya está contado en su legajo real, por eso no se suma. Se resuelven agregándolos al legajo real del mismo número desde la sección Alta Integrantes." />
                    </span>
                    <span style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', color: '#64748B', display: 'flex', alignItems: 'center', gap: 8 }}>
                      {bisLeg.toLocaleString('es-AR')} legajos
                      <span style={{ fontSize: 12, color: '#64748B' }}>
                        ({bisInteg.toLocaleString('es-AR')} integrante{bisInteg === 1 ? '' : 's'})
                      </span>
                    </span>
                  </div>
                </div>
              );
            })()}

            {/* CIERRE de la sección: representa a TODAS las personas que existen
                en padrones cerrados (cada una una vez). El embudo cuenta MIEMBROS
                de legajo y excluye bis/huérfanos; este total los suma para que la
                sección refleje el universo real de cerrados. */}
            <CerradosReconciliacion data={d} />
          </div>
        </>
      )}
    </section>
  );
}

/**
 * Cierre de la sección Legajos: representa a TODAS las personas que existen en
 * padrones CERRADOS, cada una contada una sola vez. El embudo de arriba cuenta
 * MIEMBROS de legajo (duplica a quien está en dos legajos) y no incluye los bis
 * ni los huérfanos; este bloque los suma para que el total cuadre con el
 * universo real de cerrados (≈ "Asociados OK" de padrones cerrados):
 *   total = enEmbudo + soloBis + bloqueados + bajas + huérfanos.
 * `bloqueados` (sacados del alta por la lista EUGE) y `bajas` (baja en Binary)
 * NO son anomalías: se muestran aparte de los huérfanos reales.
 */
function CerradosReconciliacion({ data }: { data: DashboardLegajosGlobal }) {
  const c = data.cerradosReconciliacion;
  if (!c || c.total <= 0) return null;
  const bajas = c.bajas ?? 0;
  // `bajas` es subconjunto de `huerfanos` (los dados de baja); el ⚠️ de anomalía
  // queda solo para los huérfanos genuinos (sin legajo y sin explicación).
  const huerfanosGenuinos = Math.max(0, c.huerfanos - bajas);
  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '2px solid var(--color-border, #E2E8F0)' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          padding: '2px 8px',
        }}
      >
        <span
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: 'var(--color-element-high)',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          Personas en padrones cerrados
          <InfoTooltip texto="Personas reales en padrones cerrados, cada una contada una sola vez. El embudo de arriba cuenta miembros de legajo y no incluye los bis ni los huérfanos; este total los suma para cerrar la cuenta (coincide con los asociados OK de padrones cerrados)." />
        </span>
        <strong
          className="heading-5"
          style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--color-primary-darker)' }}
        >
          {c.total.toLocaleString('es-AR')}
        </strong>
      </div>
      <ul
        style={{
          margin: '4px 0 0',
          paddingLeft: 24,
          fontSize: 12,
          color: 'var(--color-element-mid)',
          lineHeight: 1.7,
        }}
      >
        {huerfanosGenuinos > 0 && (
          <li style={{ color: '#B45309', fontWeight: 600 }}>
            ⚠️ {huerfanosGenuinos.toLocaleString('es-AR')} sin legajo (huérfanos en padrón cerrado —
            revisar)
          </li>
        )}
      </ul>
    </div>
  );
}

/**
 * Render uniforme del paréntesis "(N personas)" que acompaña cada número
 * de legajos del dashboard. Si el total de personas viene en 0 o coincide
 * exactamente con el de legajos (legajos sin familiares = misma cifra),
 * lo omitimos para no saturar visualmente con paréntesis triviales.
 */
function PersonasParen({ total, tone }: { total: number; tone?: string }) {
  if (total <= 0) return null;
  return (
    <span
      style={{
        fontSize: 12,
        fontVariantNumeric: 'tabular-nums',
        color: tone ?? 'var(--color-element-mid)',
      }}
    >
      ({total.toLocaleString('es-AR')} persona{total === 1 ? '' : 's'})
    </span>
  );
}

/**
 * Ícono de info con tooltip nativo (hover) que explica qué significa cada paso.
 * `stopPropagation` para que un click en el ícono no dispare el toggle del
 * FunnelRow expandible.
 */
function InfoTooltip({ texto }: { texto: string }) {
  return (
    <span
      title={texto}
      aria-label={texto}
      onClick={(e) => e.stopPropagation()}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        cursor: 'help',
        color: 'var(--color-element-mid)',
        flexShrink: 0,
      }}
    >
      <Info size={13} strokeWidth={2} />
    </span>
  );
}

function FunnelRow({
  label,
  descripcion,
  count,
  countPersonas,
  pct,
  color,
  expandible,
  expanded,
  onClick,
  metric,
  tituloDescarga,
}: {
  label: string;
  descripcion?: string;
  count: number;
  countPersonas: number;
  pct: number;
  color: string;
  expandible: boolean;
  expanded: boolean;
  onClick: () => void;
  metric: string;
  tituloDescarga: string;
}) {
  return (
    <div
      onClick={onClick}
      role={expandible ? 'button' : undefined}
      tabIndex={expandible ? 0 : undefined}
      onKeyDown={(e) => {
        if (expandible && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
      style={{
        cursor: expandible ? 'pointer' : 'default',
        userSelect: expandible ? 'none' : 'auto',
        padding: '6px 8px',
        borderRadius: 6,
        background: expandible && expanded ? 'var(--color-surface-alt, #F8FAFC)' : 'transparent',
        transition: 'background 0.15s ease',
      }}
      title={expandible ? 'Click para ver el desglose' : undefined}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span
          style={{
            fontSize: 13,
            color: 'var(--color-element-high)',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          {expandible ? (
            expanded ? (
              <ChevronDown size={14} />
            ) : (
              <ChevronRight size={14} />
            )
          ) : (
            <span style={{ width: 14 }} />
          )}
          {label}
          {descripcion && <InfoTooltip texto={descripcion} />}
        </span>
        <span style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', color, display: 'flex', alignItems: 'center', gap: 8 }}>
          {count.toLocaleString('es-AR')} ({pct.toFixed(1)}%)
          <PersonasParen total={countPersonas} tone={color} />
          {count > 0 && <MetricaDownloadButton metric={metric} titulo={tituloDescarga} />}
        </span>
      </div>
      <div
        style={{
          height: 8,
          background: 'var(--color-surface-alt, #F1F5F9)',
          borderRadius: 999,
          overflow: 'hidden',
          marginLeft: 18,
        }}
      >
        <div
          style={{
            width: `${Math.max(pct, count > 0 ? 1 : 0)}%`,
            height: '100%',
            background: color,
            transition: 'width 0.3s ease',
          }}
        />
      </div>
    </div>
  );
}

function SubFunnelRenderer({
  estado,
  dashboard,
}: {
  estado: string;
  dashboard: DashboardLegajosGlobal;
}) {
  if (estado === 'en_validacion') {
    return (
      <SubFunnel
        leyenda="Paso del wizard donde se quedó cada titular."
        items={EN_VALIDACION_ITEMS}
        data={dashboard.desgloseEnValidacion}
        dataPersonas={dashboard.desgloseEnValidacionPersonas}
        total={dashboard.porEstado.en_validacion ?? 0}
        emptyText="No hay titulares en validación con datos del wizard todavía."
      />
    );
  }
  return null;
}

const EN_VALIDACION_ITEMS: Array<{
  key: keyof DesgloseEnValidacion;
  label: string;
  tone: 'success' | 'warning' | 'error' | 'info';
  metric: string;
  descripcion: string;
}> = [
  {
    key: 'enDatosTitular',
    label: 'Datos titular',
    tone: 'info',
    metric: 'wizard.enDatosTitular',
    descripcion: 'El titular todavía no validó su identidad (Renaper). Es el primer paso del wizard.',
  },
  {
    key: 'enDatosGF',
    label: 'Datos grupo familiar',
    tone: 'info',
    metric: 'wizard.enDatosGF',
    descripcion:
      'El titular ya validó su identidad, pero falta validar (Renaper) la de algún integrante del grupo familiar.',
  },
  {
    key: 'enCuestionarioSalud',
    label: 'Cuestionario de salud',
    tone: 'warning',
    metric: 'wizard.enCuestionarioSalud',
    descripcion:
      'Todas las identidades están validadas; falta completar la declaración jurada de salud del grupo.',
  },
  {
    key: 'enEleccionPlan',
    label: 'Elección de plan',
    tone: 'warning',
    metric: 'wizard.enEleccionPlan',
    descripcion:
      'Declaración de salud completa; falta elegir el plan y finalizar. Al finalizar, el legajo pasa a "Listo para alta".',
  },
];


function SubFunnel<T extends object>({
  leyenda,
  items,
  data,
  dataPersonas,
  total,
  emptyText,
}: {
  leyenda: string;
  items: Array<{
    key: keyof T;
    label: string;
    tone: 'success' | 'warning' | 'error' | 'info';
    metric: string;
    descripcion?: string;
  }>;
  data: T;
  /** Mismo shape que `data` pero con conteo de personas. */
  dataPersonas?: T;
  /** Total del estado padre para calcular porcentaje de cada bucket. */
  total: number;
  emptyText: string;
}) {
  const totalSub = items.reduce(
    (acc, it) => acc + ((data[it.key] as unknown as number) ?? 0),
    0,
  );
  return (
    <div
      style={{
        marginTop: 6,
        marginLeft: 18,
        padding: '10px 12px',
        background: 'white',
        border: '1px solid var(--color-border, #E2E8F0)',
        borderRadius: 8,
      }}
    >
      <p
        className="text-element-mid"
        style={{ fontSize: 12, margin: '0 0 8px', fontStyle: 'italic' }}
      >
        {leyenda}
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8 }}>
        {items.map((it) => {
          const valor = (data[it.key] as unknown as number) ?? 0;
          const valorPersonas = (dataPersonas?.[it.key] as unknown as number) ?? 0;
          const pct = total > 0 ? (valor / total) * 100 : 0;
          return (
            <SubKpi
              key={String(it.key)}
              label={it.label}
              descripcion={it.descripcion}
              valor={valor}
              valorPersonas={valorPersonas}
              pct={pct}
              tone={it.tone}
              metric={it.metric}
            />
          );
        })}
      </div>
      {totalSub === 0 && (
        <p className="text-element-mid" style={{ fontSize: 12, marginTop: 8, margin: 0 }}>
          {emptyText}
        </p>
      )}
    </div>
  );
}

function SubKpi({
  label,
  descripcion,
  valor,
  valorPersonas,
  pct,
  tone,
  metric,
}: {
  label: string;
  descripcion?: string;
  valor: number;
  /** Personas (titular + familiares) representadas. Si es 0, no se pinta. */
  valorPersonas?: number;
  pct: number;
  tone: 'success' | 'warning' | 'error' | 'info';
  /** Si no se pasa, no se renderiza el botón de descarga. */
  metric?: string;
}) {
  const colors = {
    success: { bg: '#F0FDF4', border: '#BBF7D0', color: '#166534' },
    warning: { bg: '#FEF3C7', border: '#FCD34D', color: '#92400E' },
    error: { bg: '#FEF2F2', border: '#FECACA', color: '#991B1B' },
    info: { bg: '#EFF6FF', border: '#BFDBFE', color: '#1E40AF' },
  }[tone];
  return (
    <div
      style={{
        padding: '8px 10px',
        background: colors.bg,
        border: `1px solid ${colors.border}`,
        borderRadius: 6,
      }}
    >
      <p
        style={{
          fontSize: 11,
          color: 'var(--color-element-mid)',
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        {label}
        {descripcion && <InfoTooltip texto={descripcion} />}
      </p>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 18, color: colors.color, fontVariantNumeric: 'tabular-nums' }}>
            {valor.toLocaleString('es-AR')}
          </strong>
          <span style={{ fontSize: 11, color: colors.color, fontVariantNumeric: 'tabular-nums' }}>
            ({pct.toFixed(1)}%)
          </span>
          {valorPersonas != null && (
            <PersonasParen total={valorPersonas} tone={colors.color} />
          )}
        </div>
        {valor > 0 && metric && <MetricaDownloadButton metric={metric} titulo={label} />}
      </div>
    </div>
  );
}

/**
 * Icono de descarga reutilizable. Hace stopPropagation para que un click
 * en el boton dentro de FunnelRow no dispare el toggle de expansion.
 * Muestra spinner mientras descarga.
 */
function MetricaDownloadButton({ metric, titulo }: { metric: string; titulo: string }) {
  const [abierto, setAbierto] = useState(false);
  function abrir(e: React.MouseEvent | React.KeyboardEvent) {
    e.stopPropagation();
    setAbierto(true);
  }
  return (
    <>
      <button
        type="button"
        onClick={abrir}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            abrir(e);
          }
        }}
        title={`Descargar Excel de ${titulo}`}
        aria-label={`Descargar Excel de ${titulo}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 22,
          height: 22,
          padding: 0,
          background: 'transparent',
          border: '1px solid var(--color-border, #E2E8F0)',
          borderRadius: 4,
          cursor: 'pointer',
          color: 'var(--color-element-mid)',
        }}
      >
        <Download size={12} />
      </button>
      {abierto && (
        <ExportModal
          tipo="metrica-legajos"
          params={{ metric }}
          titulo={`Legajos — ${titulo}`}
          onClose={() => setAbierto(false)}
        />
      )}
    </>
  );
}

/**
 * Widget independiente de Observaciones — vive fuera del widget Legajos
 * para reforzar que es un flujo de trabajo aparte (resolver una a una)
 * y no una variante más del funnel de alta. Click abre el modal con el
 * detalle por legajo.
 */
function ObservacionesWidget({
  total,
  totalPersonas,
}: {
  total: number;
  totalPersonas: number;
}) {
  const [abierto, setAbierto] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setAbierto(true)}
        className="card"
        style={{
          marginTop: '1.5rem',
          padding: '14px 16px',
          background: '#FEF3C7',
          border: '1px solid #FCD34D',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          fontSize: 14,
          width: '100%',
          textAlign: 'left',
          cursor: 'pointer',
          color: 'inherit',
          font: 'inherit',
        }}
        title="Ver detalle de las observaciones"
      >
        <AlertTriangle size={20} color="#92400E" />
        <div style={{ flex: 1 }}>
          <h2 className="heading-5" style={{ margin: 0 }}>
            Observaciones
          </h2>
          <p style={{ margin: '2px 0 0', fontSize: 13 }}>
            <strong>{total.toLocaleString('es-AR')}</strong> legajo{total === 1 ? '' : 's'} con
            observaciones pendientes de resolver
            {totalPersonas > 0 && (
              <>
                {' '}
                <PersonasParen total={totalPersonas} tone="#92400E" />
              </>
            )}
            .
          </p>
        </div>
        <span style={{ fontSize: 13, color: '#92400E', fontWeight: 600 }}>Ver detalle →</span>
      </button>
      {abierto && <ObservacionesModal onClose={() => setAbierto(false)} />}
    </>
  );
}

const CAMPO_LABEL: Record<string, string> = {
  documento: 'Documento',
  cuil: 'CUIL',
  fechaNacimiento: 'Fecha de nacimiento',
  nombres: 'Nombres',
  apellido: 'Apellido',
  apellSolt: 'Apellido',
  sexo: 'Sexo',
  estadoCivil: 'Estado civil',
  nacionalidad: 'Nacionalidad',
  tipoDoc: 'Tipo de documento',
};

const PARENTESCO_LABEL: Record<string, string> = {
  '0': 'Titular',
  '5': 'Cónyuge',
  '7': 'Hijo/a',
};

/**
 * Botón compartido para descargar el xlsx informativo del modal actual.
 * Visible solo para roles Medicus (admin / superadmin / user-medicus); el
 * backend ya restringe los endpoints a esos roles.
 */
function BotonDescargarExcel(props: {
  label: string;
  tipo: string;
  titulo: string;
  visible: boolean;
}) {
  const { label, tipo, titulo, visible } = props;
  const [abierto, setAbierto] = useState(false);
  if (!visible) return null;
  return (
    <>
      <button
        type="button"
        className="btn btn-ghost btn--sm"
        style={{ padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: 6 }}
        onClick={() => setAbierto(true)}
      >
        <Download size={14} />
        {label}
      </button>
      {abierto && <ExportModal tipo={tipo} titulo={titulo} onClose={() => setAbierto(false)} />}
    </>
  );
}

function ObservacionesModal({ onClose }: { onClose: () => void }) {
  const { user } = useAuthStore();
  const puedeDescargar = isMedicus(user?.rol ?? UserRole.USUARIO_EMPRESA);
  const q = useQuery({
    queryKey: ['observaciones-pendientes'],
    queryFn: listarObservacionesPendientes,
  });

  const totalObs = q.data?.reduce((acc, l) => acc + l.observaciones.length, 0) ?? 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 'min(900px, 100%)',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 50px rgba(15, 23, 42, 0.3)',
        }}
      >
        <header
          style={{
            padding: '14px 18px',
            borderBottom: '1px solid var(--color-border, #E2E8F0)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <AlertTriangle size={18} color="#92400E" />
          <div style={{ flex: 1 }}>
            <h2 className="heading-5" style={{ margin: 0 }}>
              Observaciones de titulares
            </h2>
            <p className="text-element-mid" style={{ fontSize: 12, margin: '2px 0 0' }}>
              Datos que los titulares marcaron como incorrectos. Revisá cada caso y decidí si
              corregís el valor en el padrón o descartás la observación.{' '}
              {q.data && (
                <>
                  <strong>{q.data.length.toLocaleString('es-AR')}</strong> legajos ·{' '}
                  <strong>{totalObs.toLocaleString('es-AR')}</strong> observaciones.
                </>
              )}
            </p>
          </div>
          <BotonDescargarExcel
            label="Descargar Excel"
            tipo="observaciones"
            titulo="Observaciones pendientes"
            visible={puedeDescargar}
          />
          <button
            type="button"
            onClick={onClose}
            className="btn btn-ghost btn--sm"
            style={{ padding: '4px 10px' }}
          >
            Cerrar
          </button>
        </header>
        <div style={{ overflowY: 'auto', padding: 16 }}>
          {q.isLoading && <p className="text-element-mid">Cargando…</p>}
          {q.isError && (
            <p className="status status-error" style={{ display: 'block' }}>
              No se pudo cargar el detalle.
            </p>
          )}
          {q.data && q.data.length === 0 && (
            <p className="text-element-mid">No hay observaciones pendientes. ✓</p>
          )}
          {q.data && q.data.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {q.data.map((l) => (
                <LegajoObservacionesRow key={l.legajoId} legajo={l} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LegajoObservacionesRow({ legajo }: { legajo: LegajoConObservaciones }) {
  return (
    <article
      style={{
        border: '1px solid var(--color-border, #E2E8F0)',
        borderRadius: 8,
        padding: '12px 14px',
        background: 'var(--color-surface-alt, #F8FAFC)',
      }}
    >
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
        <strong style={{ fontSize: 14 }}>Legajo {legajo.legajoNumero}</strong>
        <Link
          to={`/padrones/${legajo.padronId}`}
          style={{ fontSize: 12, color: 'var(--color-primary-darker)' }}
        >
          Ver padrón →
        </Link>
      </header>
      <div
        style={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        {legajo.observaciones.map((o, i) => (
          <ObservacionDetalle key={`${o.asociadoId}-${o.campo}-${i}`} obs={o} />
        ))}
      </div>
    </article>
  );
}

function ObservacionDetalle({ obs }: { obs: ObservacionPendiente }) {
  const campoLabel = CAMPO_LABEL[obs.campo] ?? obs.campo;
  const parentescoLabel = PARENTESCO_LABEL[obs.asociadoParentesco] ?? obs.asociadoParentesco;
  const sobre = [obs.asociadoNombres, obs.asociadoApellido].filter(Boolean).join(' ').trim();
  return (
    <div
      style={{
        padding: '8px 10px',
        background: 'white',
        border: '1px solid var(--color-border, #E2E8F0)',
        borderRadius: 6,
        fontSize: 13,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontWeight: 600 }}>
          {campoLabel} · {sobre || 'Asociado'}{' '}
          {parentescoLabel && (
            <span style={{ color: 'var(--color-element-mid)', fontWeight: 400, fontSize: 12 }}>
              ({parentescoLabel})
            </span>
          )}
        </span>
        <span style={{ fontSize: 11, color: 'var(--color-element-mid)' }}>
          {new Date(obs.reportadoAt).toLocaleString('es-AR')}
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 4 }}>
        <div>
          <p style={{ fontSize: 11, margin: 0, color: 'var(--color-element-mid)' }}>Valor actual</p>
          <code style={{ fontSize: 13 }}>{obs.valorActual ?? '—'}</code>
        </div>
        <div>
          <p style={{ fontSize: 11, margin: 0, color: 'var(--color-element-mid)' }}>Valor propuesto</p>
          <code style={{ fontSize: 13, color: '#166534' }}>{obs.valorPropuesto ?? '(sin valor)'}</code>
        </div>
      </div>
      {obs.mensaje && (
        <p style={{ fontSize: 12, marginTop: 6, marginBottom: 0, fontStyle: 'italic', color: 'var(--color-element-mid)' }}>
          “{obs.mensaje}”
        </p>
      )}
    </div>
  );
}

/**
 * Widget independiente de Preexistencias — vive fuera del widget Legajos
 * para separar visualmente el flujo de análisis médico del funnel de alta.
 * Click abre el modal con el detalle por integrante (sección + pregunta +
 * texto libre).
 */
function PreexistenciasWidget({
  total,
  totalPersonas,
}: {
  total: number;
  totalPersonas: number;
}) {
  const [abierto, setAbierto] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setAbierto(true)}
        className="card"
        style={{
          marginTop: '1.5rem',
          padding: '14px 16px',
          background: '#EFF6FF',
          border: '1px solid #BFDBFE',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          fontSize: 14,
          width: '100%',
          textAlign: 'left',
          cursor: 'pointer',
          color: 'inherit',
          font: 'inherit',
        }}
        title="Ver detalle de preexistencias declaradas"
      >
        <AlertTriangle size={20} color="#1E40AF" />
        <div style={{ flex: 1 }}>
          <h2 className="heading-5" style={{ margin: 0 }}>
            Preexistencias
          </h2>
          <p style={{ margin: '2px 0 0', fontSize: 13 }}>
            <strong>{total.toLocaleString('es-AR')}</strong> legajo{total === 1 ? '' : 's'} con
            preexistencias declaradas en el cuestionario de salud
            {totalPersonas > 0 && (
              <>
                {' '}
                <PersonasParen total={totalPersonas} tone="#1E40AF" />
              </>
            )}
            .
          </p>
        </div>
        <span style={{ fontSize: 13, color: '#1E40AF', fontWeight: 600 }}>Ver detalle →</span>
      </button>
      {abierto && <PreexistenciasModal onClose={() => setAbierto(false)} />}
    </>
  );
}

function PreexistenciasModal({ onClose }: { onClose: () => void }) {
  const { user } = useAuthStore();
  const puedeDescargar = isMedicus(user?.rol ?? UserRole.USUARIO_EMPRESA);
  const q = useQuery({
    queryKey: ['preexistencias-pendientes'],
    queryFn: listarPreexistenciasPendientes,
  });

  const totalPreex = q.data?.reduce((acc, l) => acc + l.preexistencias.length, 0) ?? 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 'min(960px, 100%)',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 50px rgba(15, 23, 42, 0.3)',
        }}
      >
        <header
          style={{
            padding: '14px 18px',
            borderBottom: '1px solid var(--color-border, #E2E8F0)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <AlertTriangle size={18} color="#1E40AF" />
          <div style={{ flex: 1 }}>
            <h2 className="heading-5" style={{ margin: 0 }}>
              Preexistencias declaradas
            </h2>
            <p className="text-element-mid" style={{ fontSize: 12, margin: '2px 0 0' }}>
              Cuestionarios de salud completados donde el titular o algún integrante respondió
              afirmativamente a una pregunta crítica.{' '}
              {q.data && (
                <>
                  <strong>{q.data.length.toLocaleString('es-AR')}</strong> legajos ·{' '}
                  <strong>{totalPreex.toLocaleString('es-AR')}</strong> declaraciones.
                </>
              )}
            </p>
          </div>
          <BotonDescargarExcel
            label="Descargar Excel"
            tipo="preexistencias"
            titulo="Preexistencias declaradas"
            visible={puedeDescargar}
          />
          <button
            type="button"
            onClick={onClose}
            className="btn btn-ghost btn--sm"
            style={{ padding: '4px 10px' }}
          >
            Cerrar
          </button>
        </header>
        <div style={{ overflowY: 'auto', padding: 16 }}>
          {q.isLoading && <p className="text-element-mid">Cargando…</p>}
          {q.isError && (
            <p className="status status-error" style={{ display: 'block' }}>
              No se pudo cargar el detalle.
            </p>
          )}
          {q.data && q.data.length === 0 && (
            <p className="text-element-mid">No hay preexistencias declaradas. ✓</p>
          )}
          {q.data && q.data.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {q.data.map((l) => (
                <LegajoPreexistenciasRow key={l.legajoId} legajo={l} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LegajoPreexistenciasRow({ legajo }: { legajo: LegajoConPreexistencias }) {
  const titularLabel = [legajo.titularApellido, legajo.titularNombres]
    .filter(Boolean)
    .join(' ')
    .trim();
  // Agrupar preexistencias por asociado para que el listado sea legible
  // (todas las de la misma persona juntas).
  const porAsociado = new Map<string, PreexistenciaItem[]>();
  for (const p of legajo.preexistencias) {
    const arr = porAsociado.get(p.asociadoId) ?? [];
    arr.push(p);
    porAsociado.set(p.asociadoId, arr);
  }

  return (
    <article
      style={{
        border: '1px solid var(--color-border, #E2E8F0)',
        borderRadius: 8,
        padding: '12px 14px',
        background: 'var(--color-surface-alt, #F8FAFC)',
      }}
    >
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
        <strong style={{ fontSize: 14 }}>
          Legajo {legajo.legajoNumero}{' '}
          {titularLabel && (
            <span style={{ fontWeight: 400, color: 'var(--color-element-mid)' }}>
              · {titularLabel}
            </span>
          )}
        </strong>
        <Link
          to={`/padrones/${legajo.padronId}`}
          style={{ fontSize: 12, color: 'var(--color-primary-darker)' }}
        >
          Ver padrón →
        </Link>
      </header>
      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {[...porAsociado.values()].map((items) => (
          <PreexistenciaGrupo key={items[0].asociadoId} items={items} />
        ))}
      </div>
    </article>
  );
}

function PreexistenciaGrupo({ items }: { items: PreexistenciaItem[] }) {
  const primero = items[0];
  const nombre = [primero.asociadoApellido, primero.asociadoNombres].filter(Boolean).join(' ').trim();
  const parentesco = PARENTESCO_LABEL[primero.asociadoParentesco] ?? primero.asociadoParentesco;
  return (
    <div
      style={{
        padding: '8px 10px',
        background: 'white',
        border: '1px solid var(--color-border, #E2E8F0)',
        borderRadius: 6,
      }}
    >
      <p style={{ fontSize: 12, margin: '0 0 6px', fontWeight: 600 }}>
        {nombre || 'Asociado'}{' '}
        {parentesco && (
          <span style={{ color: 'var(--color-element-mid)', fontWeight: 400 }}>
            ({parentesco})
          </span>
        )}
      </p>
      <ul style={{ margin: 0, paddingLeft: 16, fontSize: 13 }}>
        {items.map((p, i) => (
          <li key={`${p.codigo}-${i}`} style={{ marginBottom: 4 }}>
            <span style={{ color: 'var(--color-element-mid)', fontSize: 11 }}>
              {p.seccion}
            </span>{' '}
            — {p.pregunta}
            {p.detalle && (
              <span style={{ display: 'block', fontStyle: 'italic', fontSize: 12 }}>
                Detalle: {p.detalle}
              </span>
            )}
            {p.detalleExtra && (
              <span style={{ display: 'block', fontStyle: 'italic', fontSize: 12 }}>
                Tratamiento: {p.detalleExtra}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Banner del dashboard que muestra cuantos legajos tienen una cobertura
 * privada actual declarada (cobertura.tiene === 'si' en la DDJJ de algun
 * asociado). Mismo patron que el de observaciones / preexistencias —
 * click abre el modal con el detalle (proveedor + plan por integrante).
 */
function CoberturaPrivadaBanner({
  total,
  totalPersonas,
}: {
  total: number;
  totalPersonas: number;
}) {
  const [abierto, setAbierto] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setAbierto(true)}
        style={{
          marginTop: 12,
          padding: '10px 14px',
          background: '#ECFDF5',
          border: '1px solid #A7F3D0',
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          fontSize: 13,
          width: '100%',
          textAlign: 'left',
          cursor: 'pointer',
          color: 'inherit',
          font: 'inherit',
        }}
        title="Ver detalle de coberturas privadas declaradas"
      >
        <AlertTriangle size={16} color="#047857" />
        <span>
          <strong>{total.toLocaleString('es-AR')}</strong> legajos con cobertura privada declarada{' '}
          <PersonasParen total={totalPersonas} tone="#047857" />.
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#047857', fontWeight: 600 }}>
          Ver detalle →
        </span>
      </button>
      {abierto && <CoberturaPrivadaModal onClose={() => setAbierto(false)} />}
    </>
  );
}

function CoberturaPrivadaModal({ onClose }: { onClose: () => void }) {
  const q = useQuery({
    queryKey: ['cobertura-privada-pendientes'],
    queryFn: listarCoberturaPrivadaPendientes,
  });

  const totalCoberturas = q.data?.reduce((acc, l) => acc + l.coberturas.length, 0) ?? 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 'min(960px, 100%)',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 50px rgba(15, 23, 42, 0.3)',
        }}
      >
        <header
          style={{
            padding: '14px 18px',
            borderBottom: '1px solid var(--color-border, #E2E8F0)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <AlertTriangle size={18} color="#047857" />
          <div style={{ flex: 1 }}>
            <h2 className="heading-5" style={{ margin: 0 }}>
              Cobertura privada declarada
            </h2>
            <p className="text-element-mid" style={{ fontSize: 12, margin: '2px 0 0' }}>
              Legajos donde el titular o algún integrante declaró tener una cobertura privada
              (prepaga) actual en el cuestionario de salud.{' '}
              {q.data && (
                <>
                  <strong>{q.data.length.toLocaleString('es-AR')}</strong> legajos ·{' '}
                  <strong>{totalCoberturas.toLocaleString('es-AR')}</strong> declaraciones.
                </>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-ghost btn--sm"
            style={{ padding: '4px 10px' }}
          >
            Cerrar
          </button>
        </header>
        <div style={{ overflowY: 'auto', padding: 16 }}>
          {q.isLoading && <p className="text-element-mid">Cargando…</p>}
          {q.isError && (
            <p className="status status-error" style={{ display: 'block' }}>
              No se pudo cargar el detalle.
            </p>
          )}
          {q.data && q.data.length === 0 && (
            <p className="text-element-mid">No hay coberturas privadas declaradas.</p>
          )}
          {q.data && q.data.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {q.data.map((l) => (
                <LegajoCoberturaPrivadaRow key={l.legajoId} legajo={l} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LegajoCoberturaPrivadaRow({ legajo }: { legajo: LegajoConCoberturaPrivada }) {
  const titularLabel = [legajo.titularApellido, legajo.titularNombres]
    .filter(Boolean)
    .join(' ')
    .trim();
  // Agrupar por asociado para que, si dos integrantes del mismo legajo
  // declararon cobertura, se muestren agrupados bajo cada persona.
  const porAsociado = new Map<string, CoberturaPrivadaItem[]>();
  for (const c of legajo.coberturas) {
    const arr = porAsociado.get(c.asociadoId) ?? [];
    arr.push(c);
    porAsociado.set(c.asociadoId, arr);
  }

  return (
    <article
      style={{
        border: '1px solid var(--color-border, #E2E8F0)',
        borderRadius: 8,
        padding: '12px 14px',
        background: 'var(--color-surface-alt, #F8FAFC)',
      }}
    >
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
        <strong style={{ fontSize: 14 }}>
          Legajo {legajo.legajoNumero}{' '}
          {titularLabel && (
            <span style={{ fontWeight: 400, color: 'var(--color-element-mid)' }}>
              · {titularLabel}
            </span>
          )}
        </strong>
        <Link
          to={`/padrones/${legajo.padronId}`}
          style={{ fontSize: 12, color: 'var(--color-primary-darker)' }}
        >
          Ver padrón →
        </Link>
      </header>
      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {[...porAsociado.values()].map((items) => (
          <CoberturaPrivadaGrupo key={items[0].asociadoId} items={items} />
        ))}
      </div>
    </article>
  );
}

function CoberturaPrivadaGrupo({ items }: { items: CoberturaPrivadaItem[] }) {
  const primero = items[0];
  const nombre = [primero.asociadoApellido, primero.asociadoNombres].filter(Boolean).join(' ').trim();
  const parentesco = PARENTESCO_LABEL[primero.asociadoParentesco] ?? primero.asociadoParentesco;
  return (
    <div
      style={{
        padding: '8px 10px',
        background: 'white',
        border: '1px solid var(--color-border, #E2E8F0)',
        borderRadius: 6,
      }}
    >
      <p style={{ fontSize: 12, margin: '0 0 6px', fontWeight: 600 }}>
        {nombre || 'Asociado'}{' '}
        {parentesco && (
          <span style={{ color: 'var(--color-element-mid)', fontWeight: 400 }}>
            ({parentesco})
          </span>
        )}
      </p>
      <ul style={{ margin: 0, paddingLeft: 16, fontSize: 13 }}>
        {items.map((c, i) => (
          <li key={`${c.asociadoId}-${i}`} style={{ marginBottom: 4 }}>
            <strong>{c.proveedor || 'Prepaga sin especificar'}</strong>
            {c.plan && (
              <span style={{ color: 'var(--color-element-mid)' }}> · Plan: {c.plan}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * KPI agregado de la decisión final del titular (PLAN (MS1) / PLAN (MS2) /
 * CONTACTO COMERCIAL) sobre los legajos en `validado_por_titular` +
 * `en_alta_binary` — junta ambos estados para dar el panorama total sin
 * importar si todavía está pendiente de alta o ya viajó a Binary.
 *
 * Restringido a Medicus (el guard ya filtra desde el componente padre, y
 * el endpoint también valida el rol). El refresh es cada 60s, mismo cadencia
 * que el dashboard global.
 */
const ELECCION_ITEMS: Array<{
  key: keyof DesgloseEleccionDelUsuario;
  personasKey: keyof DesgloseEleccionDelUsuario;
  label: string;
  tone: 'success' | 'warning' | 'error' | 'info';
}> = [
  { key: 'planMs1', personasKey: 'planMs1Personas', label: 'PLAN (MS1)', tone: 'info' },
  { key: 'planMs2', personasKey: 'planMs2Personas', label: 'PLAN (MS2)', tone: 'success' },
  {
    key: 'contactoComercial',
    personasKey: 'contactoComercialPersonas',
    label: 'CONTACTO COMERCIAL',
    tone: 'warning',
  },
];

/**
 * Estados a nivel PERSONA (asociado), no de legajo. El dashboard es
 * legajo-céntrico; este panel expone la foto por persona: bloqueadas (EUGE),
 * de baja (Binary y/o micrositio) y con error de validación. `activas` es el
 * resto (aprox: puede haber solape chico entre baja Binary y micrositio).
 */
function EstadosPersonaWidget({
  datos,
}: {
  datos: {
    total: number;
    activas: number;
    bloqueadas: number;
    deBaja: number;
    conError: number;
    bajaBinary: number;
    bajaMicrositio: number;
  };
}) {
  const { total, activas, bloqueadas, deBaja, conError, bajaBinary, bajaMicrositio } = datos;
  // Conteos EXCLUYENTES (los calcula el backend con prioridad de baja ›
  // bloqueada › error › activa): un dado de baja no está también en bloqueadas.
  const items = [
    { label: 'Activas', valor: activas, tone: '#047857', sub: 'sin baja/bloqueo/error' },
    { label: 'De baja', valor: deBaja, tone: '#475569', sub: `${bajaBinary.toLocaleString('es-AR')} Binary · ${bajaMicrositio.toLocaleString('es-AR')} micrositio` },
    { label: 'Bloqueadas', valor: bloqueadas, tone: '#B91C1C', sub: 'Lista EUGE (sin las de baja)' },
    { label: 'Con error', valor: conError, tone: '#B45309', sub: 'Validación' },
  ];
  return (
    <div className="card" style={{ marginTop: '1.5rem' }}>
      <div className="rowBetween">
        <h2 className="heading-5">Estados por persona</h2>
        <span className="text-element-mid" style={{ fontSize: 12 }}>
          {total.toLocaleString('es-AR')} personas
        </span>
      </div>
      <p className="text-element-mid" style={{ marginTop: 4, fontSize: 13 }}>
        Estado individual de cada asociado (no del legajo): bloqueo, baja y errores de validación.
      </p>
      <div
        style={{
          marginTop: 12,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 10,
        }}
      >
        {items.map((it) => (
          <div
            key={it.label}
            style={{
              padding: '10px 12px',
              background: 'white',
              border: '1px solid var(--color-border, #E2E8F0)',
              borderRadius: 8,
            }}
          >
            <div style={{ fontSize: 12, color: 'var(--color-element-mid)' }}>{it.label}</div>
            <strong style={{ fontSize: 22, color: it.tone, fontVariantNumeric: 'tabular-nums' }}>
              {it.valor.toLocaleString('es-AR')}
            </strong>
            <div style={{ fontSize: 11, color: 'var(--color-element-mid)', marginTop: 2 }}>{it.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EleccionDelUsuarioWidget() {
  const query = useQuery({
    queryKey: ['eleccion-del-usuario'],
    queryFn: () => getEleccionDelUsuario(),
    // Cacheado server-side (TTL 60s); polleamos cada 120s. Ver nota en
    // dashboard-global (DashboardPage).
    refetchInterval: 120_000,
  });

  return (
    <div className="card" style={{ marginTop: '1.5rem' }}>
      <div className="rowBetween">
        <h2 className="heading-5">Elección del usuario</h2>
        <span className="text-element-mid" style={{ fontSize: 12 }}>
          Validado por titular + En alta Binary
        </span>
      </div>
      <p className="text-element-mid" style={{ marginTop: 4, fontSize: 13 }}>
        Cuánta gente eligió cada opción sobre el total de legajos que ya
        decidieron, sin importar si todavía están pendientes de alta o
        ya viajaron a Binary.
      </p>

      {query.isLoading && (
        <div style={{ marginTop: 12 }}>
          <CardsSkeleton count={3} />
        </div>
      )}
      {query.isError && (
        <p className="text-error" style={{ marginTop: 12 }}>
          No se pudo cargar la elección del usuario.
        </p>
      )}
      {query.data && (
        <div
          style={{
            marginTop: 12,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 10,
          }}
        >
          {ELECCION_ITEMS.map((it) => {
            const valor = query.data?.[it.key] ?? 0;
            const valorPersonas = query.data?.[it.personasKey] ?? 0;
            const total =
              (query.data?.planMs1 ?? 0) +
              (query.data?.planMs2 ?? 0) +
              (query.data?.contactoComercial ?? 0);
            const pct = total > 0 ? (valor / total) * 100 : 0;
            return (
              <SubKpi
                key={it.key}
                label={it.label}
                valor={valor}
                valorPersonas={valorPersonas}
                pct={pct}
                tone={it.tone}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
